import { addDays, endOfMonth, subYears } from "date-fns"
import { formatLocalDate } from "./format"

export const timestampToDate = (timestamp: number) => {
  const timestampDate = new Date(timestamp)

  return timestampDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

export const getDatesAnalyticsInfo = (dateMillis: number[]) => {
  if (dateMillis.length === 0) {
    return {
      startDate: "",
      endDate: "",
    }
  }

  let earliestMillis = dateMillis[0]
  let latestMillis = dateMillis[0]

  for (const millis of dateMillis) {
    if (millis < earliestMillis) {
      earliestMillis = millis
    }
    if (millis > latestMillis) {
      latestMillis = millis
    }
  }

  const earliestDate = new Date(earliestMillis)
  const latestDate = new Date(latestMillis)

  return {
    startDate: formatLocalDate(earliestDate?.toISOString() || ""),
    endDate: formatLocalDate(endOfMonth(latestDate)?.toISOString() || ""),
  }
}

export const MINUTE_IN_MS = 60 * 1000

const HOUR_IN_MS = 1000 * 60 * 60

export const DAY_IN_MS = 1000 * 60 * 60 * 24

/**
 * Calculate the number of nights between two dates
 *
 * @param {string} startdate
 * @param {string} enddate
 * @return {number}
 */
export function calculateNights(startdate: string, enddate: string): number {
  const startDate = new Date(startdate)
  const endDate = new Date(enddate)

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return 0
  }

  const diffInMs = endDate.getTime() - startDate.getTime()
  const diffInDays = diffInMs / DAY_IN_MS

  return diffInDays
}

export const areDatesWithinSixMonths = (date1: Date, date2: Date) => {
  // Calculate the difference in months
  const difference =
    (date2.getFullYear() - date1.getFullYear()) * 12 + date2.getMonth() - date1.getMonth()

  // Check if the absolute difference is less than or equal to 6
  return Math.abs(difference) < 6
}

// A "New" user is someone who created account within last 48hrs
export const checkIsNewUser = (userCreated: Date, currentTime: Date) => {
  const timeDiff = Math.abs(userCreated.getTime() - currentTime.getTime())
  const hoursDiff = timeDiff / (1000 * 60 * 60)

  return hoursDiff <= 48
}

// Calculate time format for last unread message
export const formatLastUnreadMessageTime = (timestamp: number) => {
  if (!timestamp) return ""
  const currentTime = new Date()

  const timeDiff = Math.abs(timestamp - currentTime.getTime())

  if (timeDiff < 59 * MINUTE_IN_MS) {
    const minDifference = Math.ceil(timeDiff / MINUTE_IN_MS)
    return `${minDifference} min`
  } else if (timeDiff < HOUR_IN_MS) {
    return "1 hr"
  } else if (timeDiff < 23 * HOUR_IN_MS) {
    const hourDiff = Math.ceil(timeDiff / HOUR_IN_MS)
    return `${hourDiff} hrs`
  } else if (timeDiff < DAY_IN_MS) {
    return "1 day"
  } else if (timeDiff < 10 * DAY_IN_MS) {
    const dayDiff = timeDiff / DAY_IN_MS > 10 ? "10+" : Math.ceil(timeDiff / DAY_IN_MS)
    return `${dayDiff} days`
  } else {
    return "10+ days"
  }
}

export const getDateRangeForAgeGroup = (ageGroup?: string) => {
  const now = new Date()

  switch (ageGroup) {
    case "infant":
      return {
        minDate: addDays(subYears(now, 2), 1),
        maxDate: now,
      }
    case "child":
      return {
        minDate: addDays(subYears(now, 18), 1),
        maxDate: subYears(now, 2),
      }
    case "adult":
      return {
        minDate: undefined,
        maxDate: subYears(now, 18),
      }
    default:
      return {
        minDate: undefined,
        maxDate: now,
      }
  }
}

export const isDatePassed = (date: Date) => {
  const now = new Date()
  const sameYear = date.getUTCFullYear() === now.getFullYear()
  const monthPassed = date.getUTCMonth() < new Date().getMonth()
  return sameYear && monthPassed
}
